import { getEnvironmentAppUrls } from '@dvag/dfs-constant-config/app-urls';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

import i18next from 'i18next';

import { AuthAdapter, AuthAdapterInterface } from 'service/AuthAdapter';
import { consentStatus } from 'service/model/consentStatus';
import { BottomSectionListData, SectionListData } from 'type/sectionList';

import { appNames } from '@dvag/dfs-constant-config/app-names';
import { gdprIcons } from './GdprIcon';
import { checkIcon } from '../../../../utils/util';
import { PersonInHouseholdDto } from '../../../../graphql/person/generates';
import { handleNavigateToPath } from '../../../../service/handleNavigateToPath';
import { dashboardUtil } from '../../../../service/model/dashboardUtil';
import { navigate } from '@dvag/dfs-orchestrator-client/messengers';
import { UseQueryResult } from '@tanstack/react-query';
import { KundanzuganHaushaltDetailResponse } from '../../../../type/twoFactor';

export type KundenzugangHTMLElement = HTMLElement & {
  openModal: () => void;
};

export const borderBottom = '1px solid #DEDEDE';
const kundezugangTag = 'kundenzugang-webcomponent';

const {
  ADVISOR,
  CUSTOMER_ACCESS,
  CONVERSATION_SUMMARY,
  GOLDRICHTIGE_EMPFEHLUNG,
  TRAUMBERUF,
  RECOMMENDATION_PRESENTATION,
  RECOMMENDATION_DIRECT_ACCESS,
  TRAUMBERUF_PRESENTATION,
  TRAUMBERUF_DIRECT_ACCESS,
} = dashboardUtil;

const kundenzugangSetAttribute = ({
  existentKundenzugang,
  isTwoPersonsOlderEighteen,
  householdId,
}: {
  existentKundenzugang: Element & { authenticator?: AuthAdapterInterface };
  isTwoPersonsOlderEighteen?: boolean;
  householdId: string | undefined;
}) => {
  if (existentKundenzugang) {
    existentKundenzugang.setAttribute('haushaltsid', householdId ?? '');
    if (isTwoPersonsOlderEighteen) {
      existentKundenzugang.setAttribute('familienfreigabe', 'true');
    }
    existentKundenzugang.authenticator = AuthAdapter();
  }
};

export const useUtil = (
  householdId: string | undefined,
  isTwoPersonsOlderEighteen = false,
): {
  openKundenzugangTool: () => void;
  isKundenzugangToolClosed: boolean;
  setIsKundenzugangToolClosed: Dispatch<SetStateAction<boolean>>;
  customerDetail: { kundenNr: string; keycloakId: string };
  kundenzugangPermission: boolean;
} => {
  const [isKundenzugangToolClosed, setIsKundenzugangToolClosed] = useState<boolean>(false);
  const [customerDetail, setCustomerDetail] = useState<{ kundenNr: string; keycloakId: string }>({
    kundenNr: '',
    keycloakId: '',
  });
  const kundenzugangPermission = true;
  const injectKundezugang = useCallback(() => {
    const head = document.head || document.getElementsByTagName('head')[0];
    const existentWidget = document.getElementById('kundenzugang-url');
    if (!existentWidget) {
      const widget = document.createElement('script');
      widget.id = 'kundenzugang-url';
      widget.src = getEnvironmentAppUrls().kundenzugang;
      head.appendChild(widget);
    }

    const existentKundenzugang: Element & { authenticator?: AuthAdapterInterface } =
      document.getElementsByTagName(kundezugangTag)[0];
    kundenzugangSetAttribute({ existentKundenzugang, householdId, isTwoPersonsOlderEighteen });
  }, [householdId, isTwoPersonsOlderEighteen]);
  useEffect(() => {
    injectKundezugang();
  }, [injectKundezugang]);

  useEffect(() => {
    const familienfreigabeHandler = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail.keycloakId) {
        setCustomerDetail({
          kundenNr: customEvent.detail.kundenNr,
          keycloakId: customEvent.detail.keycloakId,
        });
      }
    };

    const kundenzugangHandler = (event: Event) => {
      const customEvent = event as CustomEvent;
      setIsKundenzugangToolClosed(customEvent.detail.complete);
    };

    document.addEventListener(
      'idm-kundenzugang-component-familienfreigabe',
      familienfreigabeHandler,
    );

    document.addEventListener('idm-kundenzugang-component', kundenzugangHandler);

    return () => {
      document.removeEventListener(
        'idm-kundenzugang-component-familienfreigabe',
        familienfreigabeHandler,
      );
      document.removeEventListener('idm-kundenzugang-component', kundenzugangHandler);
    };
  }, []);

  const openKundenzugangTool = useCallback(() => {
    const kundenzugangTool = document.getElementsByTagName(
      kundezugangTag,
    )[0] as KundenzugangHTMLElement;
    if (kundenzugangTool?.openModal) {
      kundenzugangTool.openModal();
    }
  }, []);

  return {
    openKundenzugangTool,
    isKundenzugangToolClosed,
    setIsKundenzugangToolClosed,
    customerDetail,
    kundenzugangPermission,
  };
};

export const dashboardItemLists = (
  householdId: string | undefined,
  personList?: PersonInHouseholdDto[],
  personListWithConsent?: {
    personId: string;
    status: string;
  }[],
): {
  disabledBottomList: SectionListData[];
  bottomList: SectionListData[];
  householdBudgetAndContractList: SectionListData[];
  pensionAndHousingList: SectionListData[];
} => {
  const consentCount = personListWithConsent?.length !== undefined && personList?.length
    ? `(${personListWithConsent.length}/${personList.length})`
    : '';
  const privacyTitle = consentCount
    ? `${i18next.t('dashboard.bottomList.gdpr')} ${consentCount}`
    : i18next.t('dashboard.bottomList.gdpr');

  const baseBottomItems: Omit<BottomSectionListData, 'path'>[] = [
    {
      borderBottom,
      icon: 'schild',
      title: privacyTitle,
      id: 'privacy-policy',
    },
    {
      borderBottom,
      icon: 'immobilienfinanzierung',
      title: i18next.t('dashboard.bottomList.hhbAndContractOverview'),
      id: 'household-budget-and-contract',
    },
    {
      borderBottom,
      icon: 'sessel',
      title: i18next.t('dashboard.bottomList.pensionAndHousing'),
      id: 'pension-and-housing',
    },
    {
      icon: 'kalender',
      title: i18next.t('dashboard.bottomList.appointment'),
      appName: appNames.vpd,
      id: 'interview-conclusion',
    },
  ];

  const enabledPaths: Record<string, string> = {
    'privacy-policy': 'datenschutzbestimmungen',
    'household-budget-and-contract': 'household-budget-and-contract',
    'pension-and-housing': 'pension-and-housing',
    'interview-conclusion': `/haushalt/${householdId}/analyse/terminabschluss/fuenf-schritte`,
  };

  const disabledBottomList: SectionListData[] = baseBottomItems.map((item) => ({
    ...item,
    path: '',
  }));

  const bottomList: SectionListData[] = baseBottomItems.map((item) => ({
    ...item,
    path: enabledPaths[item.id],
  }));

  const householdBudgetAndContractList = [
    {
      borderBottom,
      icon: checkIcon('immobilienfinanzierung'),
      title: i18next.t('dashboard.bottomList.householdBudget'),
      path: `/haushalt/${householdId}/analyse/haushaltsbudget/ausgaben`,
      id: 'hhb',
      appName: appNames.accounting,
    },
    {
      icon: checkIcon('klemmbrett'),
      title: i18next.t('dashboard.bottomList.contract'),
      path: `/haushalt/${householdId}/analyse/haushaltsbudget/vertragsuebersicht`,
      id: 'contract-overview',
      appName: appNames.accounting,
    },
  ];

  const pensionAndHousingList = [
    {
      borderBottom,
      icon: checkIcon('sessel'),
      title: i18next.t('dashboard.bottomList.pensionInformation'),
      path: `/haushalt/${householdId}/analyse/basisdaten/staatliche-rente`,
      id: 'pension-information',
      appName: appNames.accounting,
    },
    {
      icon: checkIcon('haus'),
      title: i18next.t('dashboard.bottomList.livingSituation'),
      path: `/haushalt/${householdId}/analyse/basisdaten/wohnsituation`,
      id: 'living-situation',
      appName: appNames.accounting,
    },
  ];

  return {
    disabledBottomList,
    bottomList,
    householdBudgetAndContractList,
    pensionAndHousingList,
  };
};

export const rightCardItemList = (
  permissions: Record<string, boolean>,
  householdId: string | undefined,
  customerPortalStatus: UseQueryResult<KundanzuganHaushaltDetailResponse[], Error>,
  openKundenzugangTool: () => void,
) => {
  const { kundenzugangPermission, recommendationPermission, teambuildingPermission } = permissions;
  const pathToDashboard = `/haushalt/${householdId}/uebersicht`;

  const consentedCount =
    customerPortalStatus.data?.filter((person) => person.registrierungsstatus === 'VOLLSTAENDIG')
      .length ?? 0;
  const totalCount = customerPortalStatus.data?.length ?? 0;
  const consentCountText = totalCount > 0 ? ` (${consentedCount}/${totalCount})` : '';
  const customerPortalTitle = `${i18next.t('dashboard.bottomList.kundenzugang')}${consentCountText}`;

  const baseRightCardList = [
    {
      permission: kundenzugangPermission,
      icon: checkIcon('partnerschaft-kunde'),
      id: CUSTOMER_ACCESS,
      path: CUSTOMER_ACCESS,
      title: customerPortalTitle,
      onClick: openKundenzugangTool,
    },
    {
      icon: checkIcon('dokumente'),
      id: CONVERSATION_SUMMARY,
      path: CONVERSATION_SUMMARY,
      title: 'Gesprächszusammenfassung',
      onClick: () =>
        navigate(
          `/${appNames.sectionReport}/haushalt/${householdId}/analyse/gespraechszusammenfassung`,
        ),
    },
    {
      permission: recommendationPermission,
      icon: checkIcon('daumen-hoch'),
      id: GOLDRICHTIGE_EMPFEHLUNG,
      path: GOLDRICHTIGE_EMPFEHLUNG,
      title: 'Goldrichtige Empfehlung',
      onClick: () =>
        handleNavigateToPath(`${pathToDashboard}/sektion/${GOLDRICHTIGE_EMPFEHLUNG}`, '', ADVISOR),
    },
    {
      permission: teambuildingPermission,
      icon: checkIcon('karriereleiter'),
      id: TRAUMBERUF,
      path: TRAUMBERUF,
      title: 'Traumberuf',
      onClick: () => handleNavigateToPath(`${pathToDashboard}/sektion/${TRAUMBERUF}`, '', ADVISOR),
    },
  ];

  const rightCardList = baseRightCardList.filter((item) => item.permission !== false);

  const baseGoldrichtigeEmpfehlungList = [
    {
      id: RECOMMENDATION_PRESENTATION,
      path: '/empfehlungprasentation',
      title: 'Kunden werben Kunden (Präsentation)',
      icon: checkIcon('slideshow'),
      onClick: () =>
        handleNavigateToPath('/haushalt/:householdId/empfehlungprasentation', householdId, ADVISOR),
    },
    {
      id: RECOMMENDATION_DIRECT_ACCESS,
      path: '/weiterempfehlung',
      title: 'Kunden werben Kunden (Direkteinstieg)',
      icon: checkIcon('vorspulen'),
      onClick: () =>
        handleNavigateToPath('/haushalt/:householdId/weiterempfehlung', householdId, ADVISOR),
    },
  ];

  const goldrichtigeEmpfehlungList = recommendationPermission
    ? baseGoldrichtigeEmpfehlungList
    : rightCardList;

  const baseTraumberufList = [
    {
      id: TRAUMBERUF_PRESENTATION,
      path: '/teamaufbauprasentation',
      title: 'Traumberuf (Präsentation)',
      icon: checkIcon('slideshow'),
      onClick: () =>
        handleNavigateToPath('/haushalt/:householdId/teamaufbauprasentation', householdId, ADVISOR),
    },
    {
      id: TRAUMBERUF_DIRECT_ACCESS,
      path: '/teamaufbau',
      title: 'Traumberuf (Direkteinstieg)',
      icon: checkIcon('vorspulen'),
      onClick: () =>
        handleNavigateToPath('/haushalt/:householdId/teamaufbau', householdId, ADVISOR),
    },
  ];

  const traumberufList = teambuildingPermission ? baseTraumberufList : rightCardList;

  return {
    rightCardList,
    goldrichtigeEmpfehlungList,
    traumberufList,
  };
};

const { APPROVED, SENT, EMPTY } = consentStatus;

const { ApprovedGdpr, PendingApproval, NotSyncedGdpr } = gdprIcons;

const DecriptionKeyEnum = {
  [APPROVED]: 'approved',
  [SENT]: 'sentOut',
  [EMPTY]: 'missing',
};

const { [APPROVED]: APPROVED_DESC, [SENT]: SENT_DESC, [EMPTY]: EMPTY_DESC } = DecriptionKeyEnum;

const consentStatusMapping = {
  [APPROVED]: { Icon: ApprovedGdpr, descriptionKey: APPROVED_DESC },
  [SENT]: { Icon: PendingApproval, descriptionKey: SENT_DESC },
  [EMPTY]: { Icon: NotSyncedGdpr, descriptionKey: EMPTY_DESC },
};

export const getConsentStatus = (status: string | undefined) =>
  status && consentStatusMapping[status];

export const isPersonYoungerThan18 = (birthdate?: string | null): boolean => {
  if (!birthdate) {
    return true;
  }
  const birthdateDate = new Date(birthdate);
  const currentDate = new Date();

  const age = currentDate.getFullYear() - birthdateDate.getFullYear();
  const adultAge = 18;

  if (birthdateDate.getTime() > currentDate.getTime()) {
    return false;
  }

  if (
    currentDate.getMonth() < birthdateDate.getMonth() ||
    (currentDate.getMonth() === birthdateDate.getMonth() &&
      currentDate.getDate() < birthdateDate.getDate())
  ) {
    return age - 1 < adultAge;
  }

  return age < adultAge;
};
